import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  getCoreRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { useGetDatasetListQuery } from "@/api/dataset/useDatasetAPI";
import { useSubscription } from "@/api/subscriptions/useSubscriptionsAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { PlusIcon } from "@/assets/icons";
import { UnlockPremiumModal } from "@/components/dataset/datasetExplorer/UnlockPremiumModal";
import { columns } from "@/components/datasetManagement/columns.tsx";
import EmptyState from "@/components/datasetManagement/EmptyState";
import FilterSection from "@/components/datasetManagement/FilterSection";
import LoadingState from "@/components/datasetManagement/LoadingState";
import Pagination from "@/components/datasetManagement/Pagination";
import TableData from "@/components/datasetManagement/TableData";
import UploadDialog from "@/components/datasetManagement/UploadDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import useSessionStorage from "@/hooks/store/useSessionStorage";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDatasetFilters } from "@/hooks/useDatasetFilters";
import { usePagination } from "@/hooks/usePagination";
import { useParsingStatus } from "@/hooks/useParsingStatus";
import { cn } from "@/lib/utils";
import { CountryCode, SpatialAggregationType } from "@/types/common";
import { DATASET_SORT_TYPE } from "@/types/dataset";
import { formatStorageFromKB } from "@/utils/format";

type TABLE_KEY =
  | "name"
  | "country_id"
  | "spatial_aggregation_type"
  | "published"
  | "created_at";

type SortOrder = "ASC" | "DESC";

const SORT_PARSED: Record<TABLE_KEY, DATASET_SORT_TYPE> = {
  name: "name",
  country_id: "country",
  spatial_aggregation_type: "spatial_aggregation_type",
  created_at: "date",
  published: "status"
};

// Reverse mapping to convert from DATASET_SORT_TYPE to TABLE_KEY
const SORT_REVERSE: Record<DATASET_SORT_TYPE, TABLE_KEY> = {
  name: "name",
  country: "country_id",
  spatial_aggregation_type: "spatial_aggregation_type",
  date: "created_at",
  status: "published"
};

export interface DatasetManagementSessionData {
  searchQuery: string;
  filters: {
    selectedCountry: CountryCode | "all";
    spatialAggregation: SpatialAggregationType | "all";
    published: boolean | "all";
  };
  pageSize: number;
  currentPage: number;
  sortBy: DATASET_SORT_TYPE;
  sortOrder: SortOrder;
}

const SESSION_STORAGE_KEY = "dataset-management-page";

const DEFAULT_SESSION_DATA: DatasetManagementSessionData = {
  searchQuery: "",
  filters: {
    selectedCountry: "all",
    spatialAggregation: "all",
    published: "all"
  },
  pageSize: 10,
  currentPage: 1,
  sortBy: "date",
  sortOrder: "DESC"
};

// Helper function to check if the path is dataset management related
const isDatasetManagementPath = (pathname: string): boolean => {
  const datasetManagementPattern = /\/dataset-management(\/[^/]+)?$/;
  return datasetManagementPattern.test(pathname);
};

// Helper function to convert SortingState to sort values
const getSortValues = (
  sorting: SortingState
): { sortBy: DATASET_SORT_TYPE; sortOrder: SortOrder } => {
  if (sorting.length > 0) {
    return {
      sortBy: SORT_PARSED[sorting[0].id as TABLE_KEY],
      sortOrder: sorting[0].desc ? "DESC" : "ASC"
    };
  }
  return {
    sortBy: "date",
    sortOrder: "DESC"
  };
};

// Helper function to convert session data to SortingState
const getInitialSorting = (
  sessionData: DatasetManagementSessionData | null
): SortingState => {
  if (!sessionData?.sortBy) {
    return [];
  }
  const tableKey = SORT_REVERSE[sessionData.sortBy];
  return [
    {
      id: tableKey,
      desc: sessionData.sortOrder === "DESC"
    }
  ];
};

export default function DatasetManagementPage() {
  const location = useLocation();
  const { isStorageLimitEnabled } = useAppFeatures();

  const [isStorageLimitDialogOpen, setIsStorageLimitDialogOpen] =
    useState(false);

  const { sessionValue: sessionStorageData, setSessionItem } =
    useSessionStorage<DatasetManagementSessionData>(
      SESSION_STORAGE_KEY,
      DEFAULT_SESSION_DATA
    );

  // Initialize state from sessionStorage or use defaults
  const {
    filters,
    setFilters: originalSetFilters,
    searchQuery,
    setSearchQuery: originalSetSearchQuery,
    debouncedSearchQuery,
    isDefaultFilters
  } = useDatasetFilters({
    initialFilters: sessionStorageData?.filters ?? DEFAULT_SESSION_DATA.filters,
    initialSearchQuery:
      sessionStorageData?.searchQuery ?? DEFAULT_SESSION_DATA.searchQuery
  });

  const {
    pageSize,
    setPageSize: originalSetPageSize,
    currentPage,
    setCurrentPage: originalSetCurrentPage
  } = usePagination({
    initialPageSize:
      sessionStorageData?.pageSize ?? DEFAULT_SESSION_DATA.pageSize,
    initialPage:
      sessionStorageData?.currentPage ?? DEFAULT_SESSION_DATA.currentPage
  });

  const { isParsingActive, setIsParsingActive, setOperationId } =
    useParsingStatus({});
  const { data: subscriptions } = useSubscription({
    refetchOnMount: "always"
  });

  // Initialize sorting from sessionStorage
  const [sorting, setSorting] = useState<SortingState>(() =>
    getInitialSorting(sessionStorageData)
  );
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);

  // Get current sort values
  const { sortBy, sortOrder } = getSortValues(sorting);

  // Clear session storage when navigating away from dataset-management pages
  useEffect(() => {
    return () => {
      if (!isDatasetManagementPath(window.location.pathname)) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    };
  }, [location.pathname]);

  // Wrapped setters that also update session storage using a functional update
  const setSearchQuery = useCallback(
    (value: string | ((prev: string) => string)) => {
      const resolvedValue =
        typeof value === "function" ? value(searchQuery) : value;
      originalSetSearchQuery(resolvedValue);
      originalSetCurrentPage(1); // Reset to first page on search query change
      setSessionItem({
        ...sessionStorageData,
        searchQuery: resolvedValue
      });
    },
    [
      originalSetCurrentPage,
      originalSetSearchQuery,
      searchQuery,
      sessionStorageData,
      setSessionItem
    ]
  );

  const setFilters = useCallback(
    (value: typeof filters | ((prev: typeof filters) => typeof filters)) => {
      const resolvedFilters =
        typeof value === "function" ? value(filters) : value;
      originalSetFilters(resolvedFilters);
      originalSetCurrentPage(1); // Reset to first page on filter change
      setSessionItem({
        ...sessionStorageData,
        filters: {
          selectedCountry:
            resolvedFilters.selectedCountry ??
            sessionStorageData.filters.selectedCountry,
          spatialAggregation:
            resolvedFilters.spatialAggregation ??
            sessionStorageData.filters.spatialAggregation,
          published:
            resolvedFilters.published ?? sessionStorageData.filters.published
        }
      });
    },
    [
      filters,
      originalSetFilters,
      originalSetCurrentPage,
      setSessionItem,
      sessionStorageData
    ]
  );

  const setPageSize = useCallback(
    (value: number | ((prev: number) => number)) => {
      const resolvedValue =
        typeof value === "function" ? value(pageSize) : value;
      originalSetPageSize(resolvedValue);
      setSessionItem({
        ...sessionStorageData,
        pageSize: resolvedValue
      });
    },
    [pageSize, originalSetPageSize, setSessionItem, sessionStorageData]
  );

  const setCurrentPage = useCallback(
    (value: number | ((prev: number) => number)) => {
      const resolvedValue =
        typeof value === "function" ? value(currentPage) : value;
      originalSetCurrentPage(resolvedValue);

      setSessionItem({
        ...sessionStorageData,

        currentPage: resolvedValue
      });
    },
    [sessionStorageData, currentPage, originalSetCurrentPage, setSessionItem]
  );

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
      setSorting((prevSorting) => {
        const newSortingState =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prevSorting)
            : updaterOrValue;
        const { sortBy: newSortBy, sortOrder: newSortOrder } =
          getSortValues(newSortingState);
        setSessionItem({
          ...sessionStorageData,
          sortBy: newSortBy,
          sortOrder: newSortOrder
        });
        return newSortingState;
      });
    },
    [setSessionItem, sessionStorageData]
  );

  const { data: datasets, isLoading } = useGetDatasetListQuery(
    {
      q: debouncedSearchQuery,
      spatial_aggregation_type: filters.spatialAggregation,
      page: currentPage,
      per_page: pageSize,
      source: "user",
      sort_by: sortBy,
      sort: sortOrder,
      ...(filters.published !== "all" && { published: filters.published }),
      ...(filters.selectedCountry !== "all" && {
        country: filters.selectedCountry
      })
    },
    { refetchOnMount: "always" }
  );

  const { data: userProfile } = useGetUserProfileAPI();

  // Table configuration
  const table = useReactTable({
    data: datasets?.data || [],
    columns: columns(userProfile?.timezone),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: datasets ? datasets.meta.pagination.total_pages : 0,
    state: {
      sorting
    },
    onSortingChange: handleSortingChange
  });

  const handleOpenChangeDialog = () => {
    setIsUploadDialogOpen((prevState) => !prevState);
  };

  // Determine if we have no datasets (empty state)
  const isNoDataset = datasets?.data.length === 0 && isDefaultFilters;

  const userPermissions = useGetUserPermission();
  const isCreateDatasetGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.CREATE];

  const totalQuota =
    (subscriptions?.add_on_kb ?? 0) + (subscriptions?.base_kb ?? 0);
  const usedQuota = subscriptions?.used_kb ?? 0;

  const progress = (usedQuota / totalQuota) * 100;

  // Show parsing state if active
  if (isParsingActive) {
    return <LoadingState message="Loading..." testId="parsing-state" />;
  }

  return (
    <Dialog open={isUploadDialogOpen} onOpenChange={handleOpenChangeDialog}>
      <div className="flex flex-col h-full gap-6 p-6">
        <div className="flex flex-row items-center justify-between">
          <h1 className="text-2xl font-bold">Dataset Management</h1>
          <div className="flex items-center flex-1 gap-2 ml-auto">
            {isStorageLimitEnabled && (
              <div className="ml-auto w-[200px] mr-2.5">
                <Progress value={progress} className="h-2" />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-txtHeading">
                    {formatStorageFromKB(usedQuota)} of{" "}
                    {formatStorageFromKB(totalQuota)}
                  </p>
                  <button
                    className="text-xs font-semibold text-royal-blue hover:underline focus:outline-none"
                    onClick={() => setIsStorageLimitDialogOpen(true)}
                  >
                    Upgrade
                  </button>
                </div>
              </div>
            )}

            {isCreateDatasetGranted && (
              <DialogTrigger asChild data-testid="header-upload-button">
                <Button
                  variant="default"
                  className={cn(!isStorageLimitEnabled && "ml-auto")}
                  disabled={progress === 100}
                >
                  <PlusIcon className="mr-2 fill-white" />
                  Upload Dataset
                </Button>
              </DialogTrigger>
            )}
          </div>
        </div>

        <FilterSection
          disabled={isNoDataset && !isLoading}
          filters={filters}
          setFilters={setFilters}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />

        {isLoading ? (
          <LoadingState message="Searching..." testId="searching-state" />
        ) : isNoDataset ? (
          <EmptyState isCreateDatasetGranted={isCreateDatasetGranted} />
        ) : (
          <TableData table={table} />
        )}

        {!(isLoading || isNoDataset) && (
          <Pagination
            table={table}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            totalData={datasets?.meta.pagination.total || 0}
          />
        )}

        <UploadDialog
          setOperationId={setOperationId}
          setIsParsingActive={setIsParsingActive}
        />

        {/* Storage Limit Dialog */}
        {isStorageLimitDialogOpen && (
          <UnlockPremiumModal
            title="Storage Limit Reached"
            description="Your current plan has reached its dataset quota. To upload more or access premium datasets, please upgrade your plan."
            contactUsDesc="Contact your admin or reach out to our sales team to get started."
            emailSubject="Request to Add Quota"
            emailBody={`Hi BVT team,\n\nI would like to request an increase in my data upload quota.\n\nPlease share the details regarding the process.\n\nThanks!`}
            onClose={() => setIsStorageLimitDialogOpen(false)}
          />
        )}
      </div>
    </Dialog>
  );
}
